
import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    IconButton
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import AxiosInstance from "../../services/AxiosInstance";
import { STATUS_OPEN_ID, STATUS_CLOSED_ID } from "../../Constants/StatusConstants";

import {
    USERS_URL,
    IMPACTS_URL,
    CHANGE_STATUS_URL,
    CHANGE_REQUEST_URL
} from "../../Constants/Constants";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { useAlert } from "../alerts/AlertContext";
import { SupportAgent } from "@mui/icons-material";


const ChangeTaskDialog = ({
    open,
    onClose,
    changeRequestId,
    taskId,
    viewMode = false,
    onSaved
}) => {

    const showAlert = useAlert();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] =
        useState(false);

    const [loadingMasters, setLoadingMasters] =
        useState(false);

    const [users, setUsers] =
        useState([]);

    const [comments, setComments] =
        useState([]);

    const [changeStatuses, setChangeStatuses] =
        useState([]);

    const [priorities, setPriorities] =
        useState([]);


    const [form, setForm] = useState({
        title: "",
        description: "",
        due: "",
        assignee: "",
        statusId: "",
        priority: "",
        comments: "",
        changeStatusId: ""
    });


    /*
     * Reset form
     */
    const resetForm = () => {

        setForm({
            title: "",
            description: "",
            due: "",
            assignee: "",
            statusId: "",
            priority: "",
            comments: "",
            changeStatusId: ""
        });

        setUsers([]);
    };


    /*
     * Load master data
     */
    const loadMasters = async () => {

        setLoadingMasters(true);

        try {

            const [
                usersResponse,
                impactsResponse,
                changeStatusResponse
            ] = await Promise.all([

                AxiosInstance.get(
                    USERS_URL
                ),

                AxiosInstance.get(
                    IMPACTS_URL
                ),

                AxiosInstance.get(
                    CHANGE_STATUS_URL
                )

            ]);

            const userData =
                usersResponse.data?.data ??
                usersResponse.data ??
                [];

            const impactData =
                impactsResponse.data?.data ??
                impactsResponse.data ??
                [];

            const changeStatusData =
                changeStatusResponse.data?.data ??
                changeStatusResponse.data ??
                [];

            setUsers(
                Array.isArray(userData)
                    ? userData
                    : []
            );

            setPriorities(
                Array.isArray(impactData)
                    ? impactData
                    : []
            );


            setChangeStatuses(Array.isArray(changeStatusData)
                ? changeStatusData
                : [])


        } catch (error) {

            console.error(
                "Unable to load task masters",
                error
            );

            showAlert(
                "Unable to load task master data",
                "error"
            );

        } finally {

            setLoadingMasters(false);
        }
    };

    const formatDueForInput = (value) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const year = date.getFullYear();
        const month = String(
            date.getMonth() + 1
        ).padStart(2, "0");
        const day = String(
            date.getDate()
        ).padStart(2, "0");
        const hours = String(
            date.getHours()
        ).padStart(2, "0");
        const minutes = String(
            date.getMinutes()
        ).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const loadTask = async (id) => {

        setLoading(true);

        try {

            const response = await AxiosInstance.get(
                `${CHANGE_REQUEST_URL}/${changeRequestId}/tasks/${taskId}`
            );

            const task =
                response.data?.data ||
                response.data;

            console.log("Task:", task);

            setForm({
                title: task.title || "",
                description: task.description || "",
                due: task.due,
                assignee: task.assignee || "",
                statusId: task.statusId || "",
                priority: task.priority || "",
                comments: task.comments || "",
                changeStatusId: task.changeStatusId || ""
            });

        } catch (error) {

            console.error(
                "Unable to load task",
                error
            );

            showAlert(
                "Unable to load task",
                "error"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
     * Open dialog
     */
    useEffect(() => {

        if (open) {

            resetForm();

            loadMasters();
        }

    }, [open]);

    useEffect(() => {

        if (!open) {
            return;
        }

        if (taskId) {
            loadTask(taskId);
        } else {
            resetForm();
        }

    }, [open, taskId]);

    /*
     * Handle field changes
     */
    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setForm(prev => ({
            ...prev,
            [name]: value
        }));
    };


    /*
     * Assignee
     */
    const selectedAssignee =
        users.find(
            user =>
                String(user.id) ===
                String(form.assignee)
        ) || null;


    const handleAssigneeChange =
        (_, newValue) => {

            setForm(prev => ({
                ...prev,
                assignee:
                    newValue?.id || ""
            }));
        };


    /*
     * Validation
     */
    const validate = () => {

        if (!form.title.trim()) {

            showAlert(
                "Task title is required",
                "error"
            );

            return false;
        }

        return true;
    };


    /*
     * Save Task
     */
    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        if (!changeRequestId) {

            showAlert(
                "Change Request ID is missing",
                "error"
            );

            return;
        }


        setSaving(true);

        try {

            /*
             * IMPORTANT:
             *
             * changeRequestId is sent to backend
             * so backend can attach this task
             * to the Change Request.
             */
            const payload = {

                title:
                    form.title.trim(),

                description:
                    form.description.trim() ||
                    null,

                due:
                    form.due || null,

                assignee:
                    form.assignee
                        ? String(form.assignee)
                        : null,

                comments:
                    form.comments
                        ? String(form.comments)
                        : null,

                priority:
                    form.priority
                        ? Number(form.priority)
                        : null,

                statusId: STATUS_OPEN_ID,

                changeStatusId:
                    form.changeStatusId
                        ? Number(form.changeStatusId)
                        : null,

                changeRequestId:
                    Number(changeRequestId)
            };


            const response =
                await AxiosInstance.post(
                    `${CHANGE_REQUEST_URL}/${changeRequestId}/tasks`,
                    payload
                );

            taskId ?
                showAlert(
                    "Task updated successfully",
                    "success"
                ) : showAlert(
                    "Task added successfully",
                    "success"
                )


            onSaved?.(
                response.data
            );


            handleClose();


        } catch (error) {

            console.error(
                "Unable to create task",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to create task",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    /*
     * Close
     */
    const handleClose = () => {

        if (saving) {
            return;
        }

        resetForm();

        onClose?.();
    };


    return (
        <ThemeProvider>
            <Dialog
                open={open}
                onClose={saving ? undefined : handleClose}
                fullWidth
                maxWidth="md"
            >

                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >

                    <Typography
                        variant="h6"
                        fontWeight={600}
                    >
                        {viewMode
                            ? "View Task"
                            : taskId
                                ? "Edit Task"
                                : "Add Task"}
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        disabled={saving}
                    >
                        <CloseIcon />
                    </IconButton>

                </DialogTitle>


                <DialogContent dividers>

                    {loadingMasters ? (

                        <Box
                            sx={{
                                minHeight: 250,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >

                            <CircularProgress />

                        </Box>

                    ) : (

                        <Stack spacing={2.5}>

                            {/* Task Title */}

                            <TextField
                                fullWidth
                                required
                                label="Task Title"
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                placeholder="Enter task title"
                                disabled={viewMode}
                            />

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "1fr 1fr"
                                    },
                                    gap: 2
                                }}
                            >
                                <Autocomplete
                                    fullWidth
                                    options={changeStatuses}
                                    value={
                                        changeStatuses.find(
                                            status => Number(status.id) === Number(form.changeStatusId)
                                        ) || null
                                    }
                                    getOptionLabel={(option) => option.name || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        Number(option.id) === Number(value.id)
                                    }
                                    onChange={(event, value) => {
                                        setForm(prev => ({
                                            ...prev,
                                            changeStatusId: value?.id || null
                                        }));
                                    }}
                                    disabled={viewMode}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Stage"
                                            required
                                        />
                                    )}
                                />

                                <FormControl fullWidth>

                                    <InputLabel>
                                        Priority
                                    </InputLabel>

                                    <Select
                                        disabled={viewMode}
                                        name="priority"
                                        value={
                                            form.priority
                                        }
                                        label="Priority"
                                        onChange={
                                            handleChange
                                        }
                                    >

                                        <MenuItem value="">
                                            Select Priority
                                        </MenuItem>

                                        {priorities.map(
                                            priority => (

                                                <MenuItem
                                                    key={
                                                        priority.id
                                                    }
                                                    value={
                                                        priority.id
                                                    }
                                                >
                                                    {
                                                        priority.name
                                                    }
                                                </MenuItem>

                                            )
                                        )}

                                    </Select>

                                </FormControl>
                            </Box>
                            {/* Description */}

                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                label="Description"
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Describe the task"
                                disabled={viewMode}
                            />


                            {/* Due + Assignee */}

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "1fr 1fr"
                                    },
                                    gap: 2
                                }}
                            >

                                <Autocomplete
                                    fullWidth
                                    disabled={viewMode}
                                    options={
                                        Array.isArray(users)
                                            ? users
                                            : []
                                    }
                                    value={
                                        selectedAssignee
                                    }
                                    isOptionEqualToValue={(
                                        option,
                                        value
                                    ) =>
                                        String(option?.id) ===
                                        String(value?.id)
                                    }
                                    getOptionLabel={(
                                        option
                                    ) =>
                                        option?.name ||
                                        option?.username ||
                                        option?.email ||
                                        ""
                                    }
                                    onChange={
                                        handleAssigneeChange
                                    }
                                    renderInput={(
                                        params
                                    ) => (

                                        <TextField
                                            {...params}
                                            label="Assignee"
                                        />

                                    )}
                                />

                                <TextField
                                    fullWidth
                                    disabled={viewMode}
                                    type="date"
                                    label="Due Date"
                                    name="due"
                                    value={form.due}
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />

                            </Box>


                            {/* Description */}
                            {form.statusId === STATUS_CLOSED_ID && (
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    label="Comments"
                                    name="comments"
                                    value={form.comments}
                                    onChange={handleChange}
                                    placeholder="Comments"
                                    disabled={viewMode}
                                />)}

                        </Stack>

                    )}

                </DialogContent>


                <DialogActions
                    sx={{
                        px: 3,
                        py: 2
                    }}
                >

                    <Button
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Cancel
                    </Button>

                    {!viewMode && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={
                                saving ||
                                loadingMasters
                            }
                            startIcon={
                                saving
                                    ? (
                                        <CircularProgress
                                            size={18}
                                        />
                                    )
                                    : null
                            }
                        >

                            {saving
                                ? "Saving..."
                                : taskId ? "Update Task" : "Add Task"}

                        </Button>
                    )}

                </DialogActions>

            </Dialog>
        </ThemeProvider>
    );
};

export default ChangeTaskDialog;

